/**
 * Dealership 360 · Iter-153 · Admin Feature-Toggle Panel
 *
 * Per-company on/off for granular Dealership 360 sub-modules. Sidebar and
 * Dealership tab list respect these. Only Business Owner / Admin can save.
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Save, Sparkles, Info } from "lucide-react";

const TOGGLES = [
  { key: "cancellations",      label: "Cancellations & Refunds", note: "Booking cancel workflow + SLA-based refund queue" },
  { key: "service_desk",       label: "Service Desk",            note: "Auto reminders · paid service quotes · job cards" },
  { key: "cash_desk",          label: "Cash Desk",               note: "Uncollected receipt queue for cashier" },
  { key: "price_lists",        label: "Price Lists",             note: "Effective-dated price rows for vehicles/accessories" },
  { key: "discount_approvals", label: "Discount Approvals",      note: "Reporting-authority approval of >5% discounts" },
  { key: "job_cards",          label: "Job Cards (After-Sales)", note: "Workshop / service ticket lifecycle" },
  { key: "rto",                label: "RTO Module",              note: "Registration workflow + fee capture" },
  { key: "finance",            label: "Finance Partners",        note: "EMI calculator + loan application inbox" },
  { key: "insurance",          label: "Insurance Partners",      note: "Policy issuance + renewal reminders" },
  { key: "waitlist",           label: "Waitlist",                note: "Auto-queue for out-of-stock models" },
  { key: "deliveries",         label: "Deliveries",              note: "eSign + gate pass + security OTP" },
  { key: "van_sales",          label: "Van Sales",                note: "Beat plans / route sales (default OFF for pure dealers)" },
  { key: "distribution",       label: "Distribution",             note: "Multi-warehouse distribution (default OFF for pure dealers)" },
];

export default function FeatureTogglesPanel() {
  const [toggles, setToggles] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/dealership/feature-toggles");
      setToggles(r.data || {});
      setDirty(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load toggles"); }
  };
  useEffect(() => { load(); }, []);

  const flip = (k, v) => { setToggles({ ...toggles, [k]: v }); setDirty(true); };

  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/dealership/feature-toggles", toggles);
      toast.success("Feature toggles saved — reload the app to refresh sidebar");
      setDirty(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  if (!toggles) return <div className="text-muted-foreground p-6">Loading feature toggles…</div>;

  return (
    <div className="space-y-4" data-testid="feature-toggles-panel">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-1"><Sparkles className="w-3 h-3" /> Feature Toggles</div>
          <div className="text-sm text-muted-foreground mt-1">Fine-grained per-company on/off for Dealership 360 sub-modules.</div>
        </div>
        <Button onClick={save} disabled={!dirty || busy} className="rounded-sm bg-accent hover:bg-accent/90 text-white gap-1.5" data-testid="feature-toggles-save">
          <Save className="w-3.5 h-3.5" /> {busy ? "Saving…" : "Save toggles"}
        </Button>
      </div>

      <div className="border rounded-sm p-3 bg-blue-50/50 text-[11px] text-blue-900 flex items-start gap-2">
        <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        <div>
          Toggles change what shows in the sidebar and in the Dealership 360 tab list. Sales-cycle collections
          are <b>never deleted</b>; a disabled toggle only hides the UI. Turn a toggle back on anytime and history is instantly visible again.
        </div>
      </div>

      <div className="card-sharp divide-y">
        {TOGGLES.map((t) => (
          <div key={t.key} className="flex items-center gap-3 p-3" data-testid={`toggle-row-${t.key}`}>
            <div className="flex-1">
              <div className="text-sm font-medium">{t.label}</div>
              <div className="text-[11px] text-muted-foreground mt-0.5">{t.note}</div>
            </div>
            <Switch checked={!!toggles[t.key]} onCheckedChange={(v) => flip(t.key, v)} data-testid={`toggle-${t.key}`} />
            <div className="w-14 text-right text-[10px] font-mono uppercase">
              {toggles[t.key] ? <span className="text-emerald-600">ENABLED</span> : <span className="text-muted-foreground">OFF</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
